import { useEffect, useRef, useState, useContext, BaseSyntheticEvent } from "react";
import GridColumn from "./grid-column";
import GridDataContext from "core/providers/grid-data-context";
import TimeShade from "./time-shade";
import { getCurrentLocalizedTimeString } from "core/lib/time-methods";
import { AppContext } from "core/providers/tv-guide.context";
import { observer } from "mobx-react-lite";
import { DimensionBlock } from "core/providers/grid.store";
import Loader from "components/loader/loader";
import context from "core/tv-guide.context";

type GridBodyProps = {
  //These apply to the TV Grid. Note GridBody has it's own version of these values
  setScrollX: (x: number) => void;
  setScrollY: (y: number) => void;
  scrollX: number;
  scrollY: number;

  dates: Date[];
  dimensionBlock: DimensionBlock;
  timeWidth: number;
  gridScrollIndexes: number[];
}

const GridBody = observer((props: GridBodyProps) => {
  const appContext = useContext(AppContext);
  const gridContext = useContext(GridDataContext);

  const store = appContext.store;

  const adjustedCurrentTime = getCurrentLocalizedTimeString();

  // Elements
  const gridMain = useRef<any>(null);

  // States
  const [dragging, setIsDragging] = useState(false);
  const [scrollX, setScrollX] = useState(0); //updated onScroll
  const [scrollY, setScrollY] = useState(0); //updated onScroll
  const [columnHeight, setColumnHeight] = useState(0);
  const hasRun = useRef<boolean>(false);

  //Calculate the position of the what's on button based on how many channels are selected
  // Default is 360px (60 * 6)

  const channelTags = context.channelTags;

  const parsedChannelTags = channelTags ? JSON.parse(channelTags) : [];

  const channelCount = parsedChannelTags.length;

  const dimensionBlock = props.dimensionBlock as DimensionBlock;
  const { channelBlock, timeChunk } = dimensionBlock;

  const backButtonPosition = channelCount < 8
    ? channelCount < 2
      ? channelBlock.height * (channelCount + 2)
      : channelBlock.height * ((channelCount / 2) + 2)
    : channelBlock.height * 6;

  const currentX = props.timeWidth - timeChunk.width * 2;

  const scrollTo = (x: number, y: number, checkX = false) => {
    const el = (gridMain.current as HTMLElement);

    //timeout is a hack
    if (!el) {
      setTimeout(() => scrollTo(x, y), 100);
      return;
    }

    el.scrollTo({
      top: y,
      left: x,
      behavior: "smooth"
    });

    setTimeout(() => {
      if (el.clientWidth === 0 || (checkX && el.scrollLeft !== x)) {
        setTimeout(() => scrollTo(x, y), 100);
        return;
      }
    }, 100);
  }

  const scrollToCurrentTime = () => {
    console.debug("Scroll to currentTime");
    scrollTo(currentX, 0, true);
  }

  useEffect(() => {
    if (!dimensionBlock || columnHeight > 0) {
      return;
    }

    const colHeight = store.channelNumbers.length * channelBlock.height;
    setColumnHeight(colHeight);
  }, [store.channelNumbers, channelBlock.height, dimensionBlock, columnHeight]);

  useEffect(() => {
    if (!gridMain.current || columnHeight === 0 || hasRun.current === true) {
      return;
    }

    //Scroll to current time on first load
    const timeOutMs = 0;
    setTimeout(() => {
      scrollTo(currentX, 0, true);
      hasRun.current = true;
    }, timeOutMs);

  }, [gridMain, columnHeight, currentX]);

  useEffect(() => {
    const dateIndex = gridContext.dateIndex;

    if (dateIndex === 0 && hasRun.current) {
      scrollToCurrentTime();
      return;
    }

    appContext.extendDays(dateIndex + 1);
  }, [appContext, gridContext.dateIndex]);

  useEffect(() => {
    const dateIndex = gridContext.getDateIndex();

    if (dateIndex === gridContext.dateIndex) {
      return;
    }

    gridContext.updateDateIndex(dateIndex);
  }, [props.gridScrollIndexes]);

  useEffect(() => {
    if (!gridMain?.current) {
      return;
    }

    // Scroll to emitted X and current Y
    if (props.scrollX !== undefined && props.scrollX !== scrollX) {
      scrollTo(props.scrollX, scrollY);
    }

    // Scroll to emitted Y and current X
    if (props.scrollY !== undefined && props.scrollY !== scrollY) {
      scrollTo(scrollX, props.scrollY);
    }
  }, [props.scrollX, props.scrollY, scrollX, scrollY]);

  const move = (ev: any) => {
    if (!dragging) {
      return;
    }

    gridMain.current.scrollBy(-ev.movementX, store.channelNumbers?.length >= 8 ? -ev.movementY : 0);
  }

  const channels = store.showFavourites
    ? store.favouriteChannelNumbers
    : store.channelNumbers;

  const onScroll = (e: BaseSyntheticEvent) => {
    const target = e.target;

    const targetObj = [
      target.scrollTop,
      target.scrollLeft,
      target.scrollWidth,
      target.clientHeight,
    ];

    gridContext.updateScrollParams(targetObj);

    setScrollX(target.scrollLeft);

    const top = target.scrollTop + gridMain.current.getBoundingClientRect().height;

    if (top < columnHeight) {
      setScrollY(target.scrollTop);
    }

    props.setScrollX(target.scrollLeft);
    props.setScrollY(target.scrollTop);
  }

  const show = columnHeight > 7 && channels.length > 0;
  let whatsOnCss = "back-button";

  if (show) {
    // The width of the current time - 1 hour < scrollX - 30 minutes;
    if ((props.timeWidth - dimensionBlock.timeChunk.width * 2) < (scrollX - dimensionBlock.timeChunk.width)) {
      whatsOnCss += " fade-in";
    }

    if ((props.timeWidth - dimensionBlock.timeChunk.width * 8) > scrollX) {
      whatsOnCss += " fade-in-reverse";
    }
  }

  let gridCss = "grid-main-panel grabbable";
  gridCss += store.hasFavourites || store.channelNumbers?.length < 8 ? " grid-trim" : ""

  return (
    <>
      <div className="scroll-handle"></div>
      <div
        className={gridCss}
        id="gridMain"
        ref={gridMain}
        onMouseDown={() => {
          if (!dragging) {
            setIsDragging(true);
          }
        }}
        onMouseMove={move}
        onMouseLeave={() => {
          if (dragging) {
            setIsDragging(false);
          }
        }}
        onMouseUp={() => {
          if (dragging) {
            setIsDragging(false);
          }
        }}
        onScroll={(e: BaseSyntheticEvent) => onScroll(e)}>
        <div className="grid-width-guide"></div>
        {show && store.dates.map((dateItem: Date, dateIndex: number) => {
          const gridCheckStart = gridContext.horizontalScrollIndexes[0] - 48 * dateIndex;
          const gridCheckEnd = gridContext.horizontalScrollIndexes[1] - 48 * dateIndex;

          const regularBool = gridCheckEnd >= 0 && 0 <= gridCheckStart && gridCheckStart <= 48;

          const startPlusEnd = gridCheckStart + gridCheckEnd;

          const midnightBool =
            gridCheckStart !== 0 &&
            gridCheckEnd !== 0 &&
            startPlusEnd < 12 &&
            startPlusEnd > -12;

          if (!regularBool && !midnightBool) {
            return null;
          }

          return (
            <GridColumn
              key={dateItem.toDateString()}
              channelList={channels}
              adjustedCurrentTime={adjustedCurrentTime}
              dateItem={dateItem}
              gridCheck={[gridCheckStart, gridCheckEnd]}
              columnHeight={columnHeight}
              scrollParams={[scrollY, scrollX]} />
          );
        })}

        <Loader display={columnHeight === 0} />

        <TimeShade
          hasFavourites={store.hasFavourites}
          scrollParams={[scrollY, scrollX]}
          timeWidth={props.timeWidth} />
      </div>

      {show && <button
        type="button"
        className={whatsOnCss}
        style={{ transform: `translate(-50%, ${backButtonPosition}px` }}
        onClick={() => {
          scrollToCurrentTime();
        }}>
        {context.dictionary.whatsOnNow}
      </button>}
    </>
  );
});

export default GridBody;
